import * as React from 'react'

import {
  type BooleanBlock as TBooleanBlock,
  getBlockUpdateOverride,
  type Block,
  getValueBasedOnSelectedVariants,
} from '@basehub/data/replicache/index'
import * as RadioGroup from '@radix-ui/react-radio-group'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useBlockInputBaseProps } from '../block-wrapper'
import { clsx } from 'clsx'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useTabsStore } from '../../tabs/store'
import { useEditorMode } from '../../editor-mode-provider'

const classes = {
  itemContainer: (active: boolean) =>
    clsx(
      'group flex items-center gap-2 group-[[data-disabled]]/root:pointer-events-none',
      active || 'group-[[data-disabled]]/root:opacity-30'
    ),
  item: (active: boolean) =>
    clsx(
      'flex size-4 cursor-default items-center justify-center rounded-full border border-dark-elevated bg-dark-border radix-state-checked:border-dark-control-base',
      active && 'bg-dark-control-base/15'
    ),
  label: (active: boolean) =>
    clsx(
      'select-none transition-colors group-hover:text-dark-dim',
      active && 'text-dark-dim'
    ),
  indicator: 'size-2 rounded-full bg-dark-control-base',
}

export const BooleanBlockInput = ({
  block,
  parent,
  path,
  disabled,
  readOnly = false,
  ...props
}: {
  block: TBooleanBlock
  parent: Block
  path: string
  readOnly?: boolean
} & RadioGroup.RadioGroupProps) => {
  const { data } = useRepoReplicache()
  const editorMode = useEditorMode()
  const replicache = data?.replicache
  const baseProps = useBlockInputBaseProps(block)
  const isReadOnly = readOnly || editorMode === 'read'
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const undoManager = useRepoUndoManager()

  const r1 = React.useId()
  const r2 = React.useId()

  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: parent,
    block,
  })

  const handleChange = React.useCallback(
    async (value: string) => {
      if (isReadOnly) return
      const params: UpdateBlockParams = {
        id: block.id,
        instanceChildTarget: block.instanceChildTarget,
        data: getBlockUpdateOverride({
          block,
          newValue: value === 'yes',
          selectedVariants: selectedDashboardVariants,
        }),
        path,
        strategy: 'merge',
        isoNow: new Date().toISOString(),
      }
      const result = await replicache?.mutate.updateBlock(params)
      if (result?.undo) {
        const focusValueOp = getBlockFocusOp({
          block: { id: block.id, sourceBlockId: block.sourceBlockId },
          preferredTabKey: activeTabKey,
          focusType: 'block:value',
        })
        undoManager.current?.push({
          redo: [focusValueOp, { op: 'updateBlock', args: params }],
          undo: [
            focusValueOp,
            { op: 'blockBatchMutations', args: { mutations: result.undo } },
          ],
        })
      }
    },
    [
      block,
      activeTabKey,
      path,
      isReadOnly,
      replicache?.mutate,
      selectedDashboardVariants,
      undoManager,
    ]
  )

  const value: 'yes' | 'no' = React.useMemo(
    () =>
      getValueBasedOnSelectedVariants({
        block,
        variantsToFilterBy: selectedDashboardVariants,
      })
        ? 'yes'
        : 'no',
    [block, selectedDashboardVariants]
  )

  return (
    <RadioGroup.Root
      {...baseProps}
      className="flex items-center group/root gap-6 text-xs text-dark-muted"
      defaultValue="yes"
      value={value}
      onValueChange={handleChange}
      data-boolean-block
      aria-label="Yes or no"
      disabled={disabled || isReadOnly}
      {...props}
    >
      <label className={classes.itemContainer(block.value)} htmlFor={r1}>
        <RadioGroup.Item
          className={classes.item(block.value)}
          value="yes"
          id={r1}
        >
          <RadioGroup.Indicator className={classes.indicator} />
        </RadioGroup.Item>
        <span className={classes.label(block.value)}>Yes</span>
      </label>
      <label className={classes.itemContainer(!block.value)} htmlFor={r2}>
        <RadioGroup.Item
          className={classes.item(!block.value)}
          value="no"
          id={r2}
        >
          <RadioGroup.Indicator className={classes.indicator} />
        </RadioGroup.Item>
        <span className={classes.label(!block.value)}>No</span>
      </label>
    </RadioGroup.Root>
  )
}
